import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Select, Button } from '@chakra-ui/react';
import InputField from 'components/fields/InputField';
import Radio from 'components/radio/index';
import { FeeGroupAssignment, Asset, Method, FeeGroup, SimpleNewFeeGroup, isSimpleFeeGroupData } from '../types';

interface AgentFeeGroupAssignmentProps {
  assignments: FeeGroupAssignment[];
  onAdd: () => void;
  onRemove: (tempId: string) => void;
  onUpdate: (tempId: string, data: Partial<FeeGroupAssignment>) => void;
  disabled?: boolean;
}

const AgentFeeGroupAssignment: React.FC<AgentFeeGroupAssignmentProps> = ({
  assignments,
  onAdd,
  onRemove,
  onUpdate,
  disabled = false,
}) => {
  const [assetList, setAssetList] = useState<Asset[]>([]);
  const [methodsByAsset, setMethodsByAsset] = useState<Record<string, Method[]>>({});
  const [feeGroupsByAsset, setFeeGroupsByAsset] = useState<Record<string, FeeGroup[]>>({});
  const [loadingAssets, setLoadingAssets] = useState(true);
  const [loadingMethods, setLoadingMethods] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      setLoadingAssets(true);
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/datasource/assets`
      );
      setAssetList(response.data || []);
    } catch (error) {
      console.error('Error fetching assets', error);
    } finally {
      setLoadingAssets(false);
    }
  };

  const fetchMethodsAndFeeGroups = async (asset: string) => {
    if (methodsByAsset[asset]) return; // Already fetched

    try {
      setLoadingMethods(prev => ({ ...prev, [asset]: true }));
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/agent/feegroups?asset=${asset}`
      );

      const responseData = response.data?.feeGroups?.payload;
      const feeGroups: FeeGroup[] = [];
      const methods: Method[] = [];

      // Extract fee groups
      const feeGroupMap = new Set<string>();
      for (const fg of responseData.feegroups || []) {
        if (!feeGroupMap.has(fg.feegroup_id)) {
          feeGroups.push({
            id: fg.feegroup_id,
            name: fg.name,
            template: {
              fixed: fg.feetemplate?.fixed || '0',
              percentage: fg.feetemplate?.percentage || '0',
            },
          });
          feeGroupMap.add(fg.feegroup_id);
        }
      }

      // Extract methods
      const methodKeySet = new Set<string>();
      for (const provider of responseData.provider || []) {
        for (const cls of provider.classes || []) {
          const key = `${cls.method_id}-${cls.classfile}`;
          if (!methodKeySet.has(key)) {
            methods.push({
              id: cls.method_id,
              name: `${cls.method_name} (${provider.provider_name})`,
            });
            methodKeySet.add(key);
          }
        }
      }

      setMethodsByAsset(prev => ({ ...prev, [asset]: methods }));
      setFeeGroupsByAsset(prev => ({ ...prev, [asset]: feeGroups }));
    } catch (error) {
      console.error('Error fetching methods and fee groups', error);
    } finally {
      setLoadingMethods(prev => ({ ...prev, [asset]: false }));
    }
  };

  const handleAssetChange = async (assignmentTempId: string, asset: string) => {
    onUpdate(assignmentTempId, {
      asset,
      methodId: '',
      methodName: '',
      feeGroup: { mode: 'existing' },
    });
    if (asset) {
      await fetchMethodsAndFeeGroups(asset);
    }
  };

  const handleMethodChange = (assignmentTempId: string, methodId: string, asset: string) => {
    const methods = methodsByAsset[asset] || [];
    const method = methods.find(m => m.id === methodId);
    onUpdate(assignmentTempId, {
      methodId,
      methodName: method?.name || '',
    });
  };

  const handleFeeGroupModeChange = (assignmentTempId: string, mode: 'existing' | 'new') => {
    const assignment = assignments.find(a => a.tempId === assignmentTempId);
    if (assignment) {
      const currentFeeGroup = assignment.feeGroup.newFeeGroup;
      const defaultSimple: SimpleNewFeeGroup = { name: '', fixed: '0', percentage: '0' };
      const existingSimple: SimpleNewFeeGroup = isSimpleFeeGroupData(currentFeeGroup) ? currentFeeGroup : defaultSimple;

      onUpdate(assignmentTempId, {
        feeGroup: {
          ...assignment.feeGroup,
          mode,
          existingFeeGroupId: mode === 'existing' ? assignment.feeGroup.existingFeeGroupId : undefined,
          newFeeGroup: mode === 'new' ? existingSimple : undefined,
        },
      });
    }
  };

  const handleExistingFeeGroupChange = (assignmentTempId: string, feeGroupId: string, asset: string) => {
    const feeGroups = feeGroupsByAsset[asset] || [];
    const feeGroup = feeGroups.find(fg => fg.id === feeGroupId);
    const assignment = assignments.find(a => a.tempId === assignmentTempId);
    if (assignment) {
      onUpdate(assignmentTempId, {
        feeGroup: {
          ...assignment.feeGroup,
          existingFeeGroupId: feeGroupId,
          existingFeeGroupName: feeGroup?.name || '',
        },
      });
    }
  };

  const handleNewFeeGroupChange = (assignmentTempId: string, field: string, value: string) => {
    const assignment = assignments.find(a => a.tempId === assignmentTempId);
    if (assignment) {
      const currentFeeGroup = assignment.feeGroup.newFeeGroup;
      const currentSimple: SimpleNewFeeGroup = isSimpleFeeGroupData(currentFeeGroup)
        ? currentFeeGroup
        : { name: '', fixed: '0', percentage: '0' };

      onUpdate(assignmentTempId, {
        feeGroup: {
          ...assignment.feeGroup,
          newFeeGroup: {
            name: currentSimple.name,
            fixed: currentSimple.fixed,
            percentage: currentSimple.percentage,
            [field]: value,
          } as SimpleNewFeeGroup,
        },
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-navy-700">
          Fee Group Assignments <span className="text-red-500">*</span>
        </label>
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-500 disabled:opacity-50"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Assignment
        </button>
      </div>

      {assignments.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-200 p-6 text-center">
          <p className="text-sm text-gray-500">
            No fee group assignments yet. Click "Add Assignment" to configure.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {assignments.map((assignment, index) => {
            const methods = methodsByAsset[assignment.asset] || [];
            const feeGroups = feeGroupsByAsset[assignment.asset] || [];
            const isLoadingMethod = loadingMethods[assignment.asset];

            return (
              <div
                key={assignment.tempId}
                className="rounded-lg border border-gray-200 bg-gray-50 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Assignment {index + 1}
                  </span>
                  {assignments.length > 1 && (
                    <button
                      type="button"
                      onClick={() => onRemove(assignment.tempId)}
                      disabled={disabled}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {/* Asset */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      Asset <span className="text-red-500">*</span>
                    </label>
                    <Select
                      disabled={disabled || loadingAssets}
                      value={assignment.asset}
                      onChange={e => handleAssetChange(assignment.tempId, e.target.value)}
                      className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm shadow-sm"
                    >
                      <option value="">
                        {loadingAssets ? 'Loading...' : '-- Select Asset --'}
                      </option>
                      {assetList.filter(a => !a.crypto).length > 0 && (
                        <optgroup label="Fiat">
                          {assetList.filter(a => !a.crypto).map(asset => (
                            <option key={asset.name} value={asset.name}>
                              {asset.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {assetList.filter(a => a.crypto).length > 0 && (
                        <optgroup label="Crypto">
                          {assetList.filter(a => a.crypto).map(asset => (
                            <option key={asset.name} value={asset.name}>
                              {asset.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </Select>
                  </div>

                  {/* Method */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      Method <span className="text-red-500">*</span>
                    </label>
                    <Select
                      disabled={disabled || !assignment.asset || isLoadingMethod}
                      value={assignment.methodId}
                      onChange={e => handleMethodChange(assignment.tempId, e.target.value, assignment.asset)}
                      className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm shadow-sm"
                    >
                      <option value="">
                        {isLoadingMethod ? 'Loading...' : '-- Select Method --'}
                      </option>
                      {methods.map(method => (
                        <option key={method.id} value={method.id}>
                          {method.name}
                        </option>
                      ))}
                    </Select>
                  </div>

                  {/* Fee Group Mode */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      Fee Group <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-4">
                      <label className="inline-flex items-center">
                        <Radio
                          id={`fg_mode_existing_${assignment.tempId}`}
                          name={`fgMode_${assignment.tempId}`}
                          disabled={disabled}
                          checked={assignment.feeGroup.mode === 'existing'}
                          onChange={() => handleFeeGroupModeChange(assignment.tempId, 'existing')}
                        />
                        <span className="ml-1 text-xs text-gray-600">Existing</span>
                      </label>
                      <label className="inline-flex items-center">
                        <Radio
                          id={`fg_mode_new_${assignment.tempId}`}
                          name={`fgMode_${assignment.tempId}`}
                          disabled={disabled}
                          checked={assignment.feeGroup.mode === 'new'}
                          onChange={() => handleFeeGroupModeChange(assignment.tempId, 'new')}
                        />
                        <span className="ml-1 text-xs text-gray-600">New</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Fee Group Selection/Creation */}
                {assignment.feeGroup.mode === 'existing' ? (
                  <div className="mt-3">
                    <Select
                      disabled={disabled || !assignment.asset || isLoadingMethod}
                      value={assignment.feeGroup.existingFeeGroupId || ''}
                      onChange={e => handleExistingFeeGroupChange(assignment.tempId, e.target.value, assignment.asset)}
                      className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm shadow-sm"
                    >
                      <option value="">
                        {isLoadingMethod ? 'Loading...' : '-- Select Fee Group --'}
                      </option>
                      {feeGroups.map(fg => (
                        <option key={fg.id} value={fg.id}>
                          {fg.name} (fixed: {fg.template.fixed} / {fg.template.percentage}%)
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  (() => {
                    const simpleFg = isSimpleFeeGroupData(assignment.feeGroup.newFeeGroup)
                      ? assignment.feeGroup.newFeeGroup
                      : { name: '', fixed: '0', percentage: '0' };
                    return (
                      <div className="mt-3 grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-white p-3 md:grid-cols-3">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Name</label>
                          <input
                            type="text"
                            disabled={disabled}
                            value={simpleFg.name}
                            onChange={e => handleNewFeeGroupChange(assignment.tempId, 'name', e.target.value)}
                            placeholder="Fee group name"
                            className="h-9 w-full rounded-md border border-gray-300 px-2 text-sm shadow-sm outline-none"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Fixed Fee</label>
                          <input
                            type="number"
                            disabled={disabled}
                            value={simpleFg.fixed}
                            onChange={e => handleNewFeeGroupChange(assignment.tempId, 'fixed', e.target.value)}
                            placeholder="0"
                            className="h-9 w-full rounded-md border border-gray-300 px-2 text-sm shadow-sm outline-none"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Percentage (%)</label>
                          <input
                            type="number"
                            disabled={disabled}
                            value={simpleFg.percentage}
                            onChange={e => handleNewFeeGroupChange(assignment.tempId, 'percentage', e.target.value)}
                            placeholder="0"
                            className="h-9 w-full rounded-md border border-gray-300 px-2 text-sm shadow-sm outline-none"
                          />
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AgentFeeGroupAssignment;
